import React, {FC, Fragment, useEffect} from 'react';
import classNames from 'classnames';
import {LoadingSpinner} from 'components';
import {useLocale} from 'hooks';
import {
  DirectBookingProviderType,
  DirectBookingSearchProviderParams,
  SliceStatus,
  WellniteContentArticle,
} from 'interfaces';
import {Helmet} from 'react-helmet-async';
import {useTranslation} from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory} from 'react-router-dom';

import {
  getDirectBookingSearchedProviders,
  resetDirectBookingSearchedProviders,
  setDirectBookingScrollPosition,
  setDirectBookingSelectedProvider,
} from '../directBookingActions';
import {selectDirectBookingSearchedProviders} from '../directBookingSelectors';
import {DirectBookingSliceState} from '../directBookingSlice';
import DirectBookingProviderCard from '../Provider/DirectBookingProviderCard';
import {useDirectBookingFilter} from '../useDirectBookingFilter';
import {mapProviderData} from '../utils';

import {DirectBookingProvideListHeader} from './DirectBookingProvideListHeader';

let currentScrollPosition = 0;

const DirectBookingProviderList: FC = () => {
  const {locale} = useLocale();
  const isInternationalUser = locale !== 'en-us';
  const filter = useDirectBookingFilter();
  const {conditions, paymentType, state, profession, country, searchText} =
    filter.selectedOptions ?? {};
  const history = useHistory();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const {
    data = [],
    hasMore,
    status,
    offset,
    searchParams,
    scrollPosition,
  }: DirectBookingSliceState['providers'] = useSelector(
    selectDirectBookingSearchedProviders,
  );

  const loadProviders = (offset: string) => {
    const searchParams: DirectBookingSearchProviderParams = {
      conditions: conditions?.join(','),
      paymentType,
      state,
      country,
      profession,
      size: '20',
      offset: offset ?? '',
      searchText,
    };
    dispatch(getDirectBookingSearchedProviders(searchParams));
  };

  useEffect(() => {
    if (data.length < 10 && hasMore) {
      loadProviders(offset);
    }
  }, [data]);

  useEffect(() => {
    const setCurrentScroll = () => {
      dispatch(setDirectBookingScrollPosition(currentScrollPosition));
    };

    const hasChange =
      conditions?.length !== searchParams?.conditions?.length ||
      paymentType !== searchParams?.paymentType ||
      state !== searchParams?.state ||
      profession !== searchParams?.profession ||
      country !== searchParams?.country ||
      searchText !== searchParams?.searchText;

    if (!hasChange) {
      return setCurrentScroll;
    }

    dispatch(resetDirectBookingSearchedProviders());
    loadProviders('');
    history.replace('?' + filter.urlParams);

    return setCurrentScroll;
  }, [conditions?.length, paymentType, state, profession, country, searchText]);

  const config = {
    dataLength: data.length,
    hasMore: hasMore,
    next: () => loadProviders(offset),
  };

  const isLoading = () => !data.length && status === SliceStatus.pending;

  const onProviderClick = (provider: DirectBookingProviderType) =>
    dispatch(setDirectBookingSelectedProvider(provider));

  const renderProviders = () => {
    return Array.isArray(data) && data.length
      ? data
          .filter(pr => pr.nextAvailableSlot)
          .map((provider, idx) => {
            const {
              bio,
              headshot,
              fullName,
              userId,
              calendarId,
              licences,
              states,
              conditions,
              acceptedInsurances,
              audio,
              article,
              nextAvailable,
            } = mapProviderData(provider);

            return (
              <div
                key={+idx}
                className="py-3 md:p-3 relative"
                onClick={() => onProviderClick(provider)}
                onKeyDown={() => onProviderClick(provider)}
              >
                {/* Inactive Statue */}
                {!calendarId ? (
                  <span className="absolute top-6 right-3 sm:right-8 sm:top-8 bg-red-200 border border-red-200 text-rose-900 z-40 px-2 py-1 sm:px-5 sm:py-2 rounded-full text-xs sm:text-sm">
                    {t('noAvailability', 'No Availability')}
                  </span>
                ) : null}
                <Link
                  className={classNames(
                    'px-3 py-10 sm:p-8 block text-left bg-white xs:rounded-xl text-base xs:text-lg border border-transparent hover:border-black shadow-outline transition ease-in-out delay-50',
                    {
                      'pointer-events-none opacity-50': !calendarId,
                    },
                  )}
                  to={`provider/${userId}?${filter.urlParams}`}
                >
                  <DirectBookingProviderCard
                    bio={bio}
                    headshot={headshot}
                    name={fullName}
                    licences={licences}
                    states={states}
                    slim={true}
                    conditions={conditions}
                    audio={audio}
                    nextAvailable={nextAvailable}
                    article={article as WellniteContentArticle}
                    acceptedInsurances={acceptedInsurances}
                    selectedOptions={filter.selectedOptions}
                  />
                </Link>
              </div>
            );
          })
      : null;
  };

  return (
    <Fragment>
      <Helmet>
        <title>{t('title.getStarted', 'Get Started | Wellnite Health')}</title>
      </Helmet>
      <section
        className="w-screen h-screen text-left overflow-y-auto"
        id="scrollableDiv"
      >
        <DirectBookingProvideListHeader {...filter} />

        <section className="max-w-7xl xs:px-10 m-auto pb-16 text-left mx-auto mt-10 font-inter leading-[26px]">
          {isLoading() ? (
            <section className="flex flex-col items-center justify-center max-w-[780px] w-full h-full m-auto">
              <LoadingSpinner type="Hearts" height={80} displayDelay={0} />
              <p className="pt-4">
                {t('loadingProviders', 'Loading Providers...')}
              </p>
            </section>
          ) : (
            <>
              <div className="pl-3 xs:pl-0">
                {/* <h3 className="font-montserrat pb-2 font-semibold text-lg">
                  <span> {searchText} </span>
                </h3> */}
                {!isInternationalUser ? (
                  <p className="text-sm text-gray-600 pb-8">
                    {t('directory.headerText')}
                  </p>
                ) : null}
              </div>
              <InfiniteScroll
                {...config}
                loader={
                  <div className="w-full flex justify-center items-center mx-auto space-x-3 my-8">
                    <LoadingSpinner
                      type="Hearts"
                      height={50}
                      displayDelay={0}
                    />
                  </div>
                }
                initialScrollY={scrollPosition}
                onScroll={e =>
                  (currentScrollPosition = (e.target as HTMLDivElement)
                    .scrollTop)
                }
                endMessage={
                  data?.length !== 0 ? (
                    <p className="bg-[#ffcb58]/[.15] text-sm rounded-xl p-5 text-center mt-4 text-gray-600">
                      {t('directory.allProvidersLoaded')}
                    </p>
                  ) : (
                    <h4 className="w-fit mx-auto my-7 px-4 py-1 font-medium text-gray-600">
                      {t('noProvidersFound', 'No Providers Found!')}
                    </h4>
                  )
                }
                scrollableTarget="scrollableDiv"
                className="xs:-mx-6"
              >
                {renderProviders()}
              </InfiniteScroll>
            </>
          )}
        </section>
      </section>
    </Fragment>
  );
};

export default DirectBookingProviderList;
